import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Card } from "../Card";
import { formatTimestamp } from "./utils";

const RealTimeFlatReadings = ({ realTimeData }) => {
  const { t } = useTranslation();

  // Handle array response
  if (Array.isArray(realTimeData)) {
    if (realTimeData.length === 0) {
      return (
        <Card className="p-6 rounded-xl shadow-sm border border-gray-200">
          <p className="text-gray-500 text-sm">{t("admin_no_flat_readings")}</p>
        </Card>
      );
    }

    // Filter and categorize buildings with flat readings
    const buildingsWithFlatReadings = realTimeData.filter(
      (building) => building.flatReading
    );
    const blockHouses = buildingsWithFlatReadings.filter(
      (building) => building.bType === "0"
    );
    const otherBuildings = buildingsWithFlatReadings.filter(
      (building) => building.bType !== "0"
    );

    const totalCount = buildingsWithFlatReadings.length;
    const blockHousesCount = blockHouses.length;
    const otherBuildingsCount = otherBuildings.length;

    if (buildingsWithFlatReadings.length === 0) {
      return (
        <Card className="p-6 rounded-xl shadow-sm border border-gray-200">
          <p className="text-gray-500 text-sm">{t("admin_no_flat_readings")}</p>
        </Card>
      );
    }

    return (
      <Card className="p-6 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <h2 className="text-lg font-semibold text-gray-900">
            {t("admin_buildings_title")}
          </h2>
          <span className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-sm font-medium border border-red-100">
            Total: {totalCount}{" "}
            {totalCount === 1 ? t("admin_building_singular") : t("admin_buildings_plural")}
          </span>
        </div>

        {blockHousesCount > 0 && (
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
              <h3 className="text-base font-semibold text-gray-900">
                {t("admin_block_houses")}
              </h3>
              <span className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-sm font-medium border border-red-100">
                {blockHousesCount}{" "}
                {blockHousesCount === 1 ? t("admin_building_singular") : t("admin_buildings_plural")}{" "}
                {t("admin_buildings_detected")}
              </span>
            </div>
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t("admin_building_name")}
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t("admin_meter_id")}
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t("admin_latest_reading")}
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t("timestamp")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {blockHouses.map((building, index) => {
                    const latestReading =
                      building.recentReadings &&
                      building.recentReadings.length > 0
                        ? building.recentReadings[0]
                        : null;

                    return (
                      <tr
                        key={building.bGuid ?? building.meterID ?? index}
                        className="border-b border-gray-100 hover:bg-gray-50/80 transition-colors"
                      >
                        <td className="px-5 py-3 font-medium text-gray-900">
                          {building.buildingName || `${t("unknown")} ${t("admin_building_singular")}`}
                        </td>
                        <td className="px-5 py-3 text-gray-700 font-mono text-sm">
                          {building.meterID || "N/A"}
                        </td>
                        <td className="px-5 py-3 text-gray-700 tabular-nums">
                          {latestReading ? latestReading.reading : "N/A"}
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-600">
                          {latestReading
                            ? formatTimestamp(latestReading.timestamp)
                            : "N/A"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {otherBuildingsCount > 0 && (
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-4">
              <h3 className="text-base font-semibold text-gray-900">
                {t("admin_other_buildings")}
              </h3>
              <span className="px-3 py-1.5 bg-red-50 text-red-700 rounded-lg text-sm font-medium border border-red-100">
                {otherBuildingsCount}{" "}
                {otherBuildingsCount === 1 ? t("admin_building_singular") : t("admin_buildings_plural")}{" "}
                {t("admin_buildings_detected")}
              </span>
            </div>
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t("admin_building_name")}
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t("admin_meter_id")}
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t("admin_latest_reading")}
                    </th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      {t("timestamp")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {otherBuildings.map((building, index) => {
                    const latestReading =
                      building.recentReadings &&
                      building.recentReadings.length > 0
                        ? building.recentReadings[0]
                        : null;

                    return (
                      <tr
                        key={building.bGuid ?? building.meterID ?? index}
                        className="border-b border-gray-100 hover:bg-gray-50/80 transition-colors"
                      >
                        <td className="px-5 py-3 font-medium text-gray-900">
                          {building.buildingName || `${t("unknown")} ${t("admin_building_singular")}`}
                        </td>
                        <td className="px-5 py-3 text-gray-700 font-mono text-sm">
                          {building.meterID || "N/A"}
                        </td>
                        <td className="px-5 py-3 text-gray-700 tabular-nums">
                          {latestReading ? latestReading.reading : "N/A"}
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-600">
                          {latestReading
                            ? formatTimestamp(latestReading.timestamp)
                            : "N/A"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>
    );
  }

  return (
    <Card className="p-6 rounded-xl shadow-sm border border-gray-200">
      <p className="text-gray-500 text-sm">{t("admin_no_realtime_data")}</p>
    </Card>
  );
};

RealTimeFlatReadings.propTypes = {
  realTimeData: PropTypes.oneOfType([
    PropTypes.shape({
      meterID: PropTypes.string,
      buildingName: PropTypes.string,
      bGuid: PropTypes.string,
      bType: PropTypes.string,
      recentReadings: PropTypes.arrayOf(
        PropTypes.shape({
          reading: PropTypes.number,
          timestamp: PropTypes.string,
        })
      ),
      flatReading: PropTypes.bool,
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        meterID: PropTypes.string,
        buildingName: PropTypes.string,
        bGuid: PropTypes.string,
        bType: PropTypes.string,
        recentReadings: PropTypes.arrayOf(
          PropTypes.shape({
            reading: PropTypes.number,
            timestamp: PropTypes.string,
          })
        ),
        flatReading: PropTypes.bool,
      })
    ),
  ]),
};

export default RealTimeFlatReadings;
